import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import TableCSVExport from '../../../component/TableCSVExport';
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Types
import LoginsDataTable from "../../../types/LoginsDataTable";

// Helpers
import { getDefaultPageSize } from "../../../helpers/utils";

// Components.
import ReactTable from '../../../component/ReactTable';
import LoginTags from "../../../types/LoginsTags";
import ConditionalRender from "../../../helpers/ConditionalRender";
import SearchInput from '../../../component/SearchInput';

function LoginsList(props: {
    loading: boolean,
    loginDataTable: LoginsDataTable[],
    tags: LoginTags[],
    isAdmin: boolean
}) {

    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data = useMemo(() => props.loginDataTable.filter(row => ((row.name.toLowerCase().includes(tableFilter.toLowerCase())))), [props.loginDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            Header: 'Name',
            accessor: 'name',
            disableSortBy: true
        },
        {
            Header: 'Type',
            accessor: 'admin',
            defaultCanSort: true,
            Cell: ({ cell }) => (
                cell.row.values.admin ? 'Administrator' : ''
            )
        },
        {
            Header: 'User Type',
            accessor: 'sso_provider',
            defaultCanSort: true,
            Cell: ({ cell }) => (
                cell.row.values.sso_provider ? `SSO User (${cell.row.values.sso_provider})` : 'Local User'
            )
        },
        {
            Header: 'Tags',
            accessor: (row: any) => {
                if (props.tags.length) {
                    const tags = props.tags.filter(tag => tag.loginid === row.id);
                    return tags.length ? tags.map(tag => `${tag.tagname}:${tag.tagvalue}`).join('; ') : '';
                }
                return '';
            },
            id: 'tags',
            canSort: false,
            defaultCanSort: false,
            Cell: ({ value }) => value || ''
        },
        ...(props.isAdmin ? [{
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    <Link to={`/admin/logins/edit/${cell.row.values.id}`} role="button"
                          className="btn btn-sm btn-primary">
                        <i className="fal fa-edit fa-fw"/><span>Edit</span>
                    </Link>
                    <Link to={`/admin/logins/delete/${cell.row.values.id}`} role="button"
                          className="btn btn-sm btn-danger">
                        <i className="fal fa-trash-alt fa-fw"/><span>Delete</span>
                    </Link>
                </React.Fragment>
            )
        }] : [])
    ], [props.isAdmin, props.tags]);


    const tableInstance = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id'],
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
                sortBy: [{ id: 'name', desc: false }]
            }
        },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-users fa-fw" aria-hidden="true"></i>
                    Logins
                    <span className="badge bg-info"
                          data-tooltip-id="global-tooltip" data-tooltip-content="Total logins count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseLogins" aria-expanded="false" aria-controls="collapseLogins"></i>
                </div>
                <div id="collapseLogins" className="card-body collapse show">
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-10">
                            <ConditionalRender if={props.isAdmin}>
                                <Link to="/admin/logins/create" role="button" className="btn btn-sm btn-success">
                                    <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                                </Link>
                            </ConditionalRender>
                            <TableCSVExport
                                tableInstance={tableInstance}
                                filename="Logins"
                            />
                        </div>
                        <div className="col col-md-2">
                            <SearchInput
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                                autoFocus
                            />
                        </div>
                    </div>
                    <ReactTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={data.length}
                        error={null}
                        loading={props.loading}
                    />
                </div>
            </div>
        </div>
    )
}

export default LoginsList;
